import { cloneDeep } from "lodash";

import { FieldNode, PrimitiveNode } from "./types";

/*
 * The value a field gets when it is switched on or created: its schema default, else a value built from its type.
 * Required children without a default are built too, so an enabled object is complete (e.g. `translation` gets its required `model`).
 */

const primitiveFallback = (node: PrimitiveNode): unknown => {
  switch (node.input) {
    case "switch":
      return false;
    case "number":
      return node.minimum ?? 0;
    case "select":
      return node.options[0] ?? "";
    default:
      return "";
  }
};

export const buildDefault = (node: FieldNode, allowNull = true): unknown => {
  if (node.default !== undefined) {
    return cloneDeep(node.default);
  }

  if (node.nullable && allowNull) {
    return null;
  }

  switch (node.kind) {
    case "primitive":
      return primitiveFallback(node);
    case "array":
      return [];
    case "map":
      return {};
    case "oneOf": {
      const option =
        node.options.find((item) => item.value === node.defaultOption) ??
        node.options[0];
      if (!option) return {};
      return {
        [node.discriminator]: option.value,
        ...(buildDefault(option.node, false) as object),
      };
    }
    case "union": {
      const first = node.options[0];
      return first ? buildDefault(first.node, false) : null;
    }
    case "object": {
      const value: Record<string, unknown> = {};
      node.children.forEach((child) => {
        if (child.default !== undefined) {
          value[child.key] = cloneDeep(child.default);
        } else if (child.required) {
          value[child.key] = buildDefault(child);
        }
      });
      return value;
    }
  }
};
